import { MihatoMark } from "@/common/components/MihatoMark";
import { useDismiss } from "@/common/hooks/useDismiss";
import { useLogout } from "@/common/hooks/useLogout";
import type { AccountRole } from "@/features/accounts/api/accountsApi";
import { useMe } from "@/features/accounts/api/accountsApi";
import { AccountSwitcher } from "@/features/accounts/components/AccountSwitcher";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { useReadyInvoicesCount } from "@/features/received-invoices/api/receivedInvoicesApi";
import {
  BarChart3,
  CalendarClock,
  CircleDollarSign,
  Inbox,
  Layers,
  LogOut,
  MoreHorizontal,
  Settings,
  Tractor,
  Users,
} from "lucide-react";
import { useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { RoleGate } from "./RoleGate";

const ROLE_LABELS: Record<AccountRole, string> = {
  owner: "Propietario",
  admin: "Administrador",
  operator: "Operador",
  viewer: "Visualizador",
};

type Props = {
  onClose?: () => void;
};

export function AppSidebar({ onClose }: Props) {
  const logout = useLogout();
  const [menuOpen, setMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { currentMembership } = useCurrentAccount();
  const { data: me } = useMe();

  useDismiss({
    enabled: menuOpen,
    containerRef: userMenuRef,
    onDismiss: () => setMenuOpen(false),
  });

  const displayName = me?.user.displayName ?? me?.user.email ?? "";
  const userInitials = me?.user.displayName
    ? me.user.displayName.slice(0, 2).toUpperCase()
    : (me?.user.email?.split("@")[0]?.slice(0, 2).toUpperCase() ?? "..");
  const roleLabel = currentMembership ? ROLE_LABELS[currentMembership.role] : "";

  return (
    <aside className="flex h-full flex-col border-app-border border-r bg-app-surface">
      <div className="flex items-center gap-3 border-app-border border-b px-[18px] pt-[18px] pb-3.5">
        <MihatoMark className="h-9 w-9 text-app-ink-1" accent />
        <span className="font-semibold text-[16px] text-app-ink-1 leading-none tracking-[-0.01em]">
          HATO
        </span>
      </div>

      <div className="mx-3.5 mt-3.5 mb-2.5">
        <AccountSwitcher />
      </div>

      <nav className="flex-1 overflow-auto px-2.5 py-1.5">
        <div className="mt-3.5">
          <div className="px-2.5 pb-1.5 font-medium text-[10px] text-app-ink-3 uppercase tracking-[0.08em]">
            Operaciones
          </div>
          <SidebarItem to="/app/hato" icon={<Tractor size={16} />} label="Hato" onClick={onClose} />
          <SidebarItem
            to="/app/lotes"
            icon={<Layers size={16} />}
            label="Lotes"
            onClick={onClose}
          />
        </div>

        <RoleGate roles={["admin", "owner", "viewer"]}>
          <div className="mt-3.5">
            <div className="px-2.5 pb-1.5 font-medium text-[10px] text-app-ink-3 uppercase tracking-[0.08em]">
              Contabilidad
            </div>
            <SidebarItem
              to="/app/costos"
              icon={<CircleDollarSign size={16} />}
              label="Costos"
              onClick={onClose}
            />
            <RecibidosSidebarItem onClick={onClose} />
            <SidebarItem
              to="/app/depreciacion"
              icon={<CalendarClock size={16} />}
              label="Depreciación"
              onClick={onClose}
            />
            <SidebarItem
              to="/app/reportes"
              icon={<BarChart3 size={16} />}
              label="Reportes"
              onClick={onClose}
            />
          </div>
        </RoleGate>

        <RoleGate roles={["admin", "owner", "viewer"]}>
          <div className="mt-3.5">
            <div className="px-2.5 pb-1.5 font-medium text-[10px] text-app-ink-3 uppercase tracking-[0.08em]">
              Sistema
            </div>
            <RoleGate roles={["owner", "admin"]}>
              <SidebarItem
                to="/app/members"
                icon={<Users size={16} />}
                label="Miembros"
                onClick={onClose}
              />
            </RoleGate>
            <SidebarItem
              to="/app/ajustes"
              icon={<Settings size={16} />}
              label="Ajustes"
              onClick={onClose}
            />
          </div>
        </RoleGate>
      </nav>

      <div
        ref={userMenuRef}
        className="relative flex items-center gap-2.5 border-app-border border-t px-3.5 py-3"
      >
        <div className="grid h-[30px] w-[30px] place-items-center rounded-full bg-[#c8cec3] font-semibold text-[12px] text-app-ink-1">
          {userInitials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-[13px] text-app-ink-1">{displayName}</div>
          <div className="text-[11px] text-app-ink-3">{roleLabel}</div>
        </div>
        <button
          type="button"
          aria-label="Más opciones"
          onClick={() => setMenuOpen((o) => !o)}
          className="grid h-7 w-7 place-items-center rounded-md border border-app-border bg-app-surface text-app-ink-2 hover:bg-app-surface-2 hover:text-app-ink-1"
        >
          <MoreHorizontal size={14} />
        </button>

        {menuOpen && (
          <div className="absolute right-0 bottom-full z-20 mb-1 w-44 overflow-hidden rounded-xl border border-app-border bg-app-surface shadow">
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                void logout();
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-rose-600 text-sm transition-colors hover:bg-rose-50"
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

type SidebarItemProps = {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  badge?: number;
};

function SidebarItem({ to, icon, label, onClick, badge }: SidebarItemProps) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 font-medium text-[13px] transition-colors ${
          isActive
            ? "bg-app-ink-1 text-white"
            : "text-app-ink-2 hover:bg-app-surface-2 hover:text-app-ink-1"
        }`
      }
    >
      <span className="shrink-0">{icon}</span>
      <span>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span
          aria-label={`${badge} por asignar`}
          className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-app-emerald-bg px-1.5 py-0.5 font-semibold text-[11px] text-app-emerald tabular-nums"
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

// Vive dentro del RoleGate de Contabilidad: operator nunca monta el hook (el
// backend igual le daría 404 — anti-enumeración).
function RecibidosSidebarItem({ onClick }: { onClick?: () => void }) {
  const { currentAccount } = useCurrentAccount();
  const { data: readyCount } = useReadyInvoicesCount(currentAccount?.id ?? "");

  return (
    <SidebarItem
      to="/app/recibidos"
      icon={<Inbox size={16} />}
      label="Recibidos"
      onClick={onClick}
      badge={readyCount}
    />
  );
}
